"use client";
import { Check } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { BusinessFormModal } from "@/components/BusinessFormModal";
import ApartmentDibsAvatar from "@/components/ui/ApartmentDibsAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/navbar-dropdown-menu";
import { useLocalStorage } from "@/hooks/use-local-storage";
import {
  usePlatformPermissions,
  useBusinessPermissions,
  useSession,
  authClient,
} from "@/lib/auth/client";

import { getNavSectionsForUser } from "./navconfig";
// Removed segmented ToggleGroup in favor of menu-styled items for consistent UX

export function AvatarMenu({
  onContactUsAction,
  onMenuStyleChangeAction,
}: {
  onContactUsAction: () => void;
  onMenuStyleChangeAction: (menuStyle: "tenant" | "business") => void;
}) {
  const router = useRouter();
  const { data: session } = useSession();
  const user = session?.user;
  const { isLandlord, activeBusiness } = useBusinessPermissions();
  const { isPlatformAdmin } = usePlatformPermissions();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [businessModalOpen, setBusinessModalOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useLocalStorage<"tenant" | "business">(
    "apartmentdibs.menuStyle",
    "tenant",
  );
  const [justCreatedBusiness, setJustCreatedBusiness] = useState(false);
  const [optimisticAvatarUrl, setOptimisticAvatarUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  // Listen for avatar updates from AvatarForm
  useEffect(() => {
    const handleAvatarUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ url: string }>;
      setOptimisticAvatarUrl(customEvent.detail.url);
    };

    window.addEventListener("avatar-updated", handleAvatarUpdate);
    return () => {
      window.removeEventListener("avatar-updated", handleAvatarUpdate);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    // Force tenant mode if no active organization
    if (!isLandlord && menuStyle === "business") {
      setMenuStyle("tenant");
    }

    onMenuStyleChangeAction?.(menuStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLandlord, menuStyle]); // Added menuStyle to dependencies

  const hasBusinessProfile = isLandlord || justCreatedBusiness;

  // TODO: Show toggle only if user has Business associated with active organization
  // Better Auth organization plugin may add activeOrganizationId to session
  // For now, show toggle if user has business profile
  const showProfileToggle = hasBusinessProfile;

  // Get navigation sections based on user roles
  const userRoles = user?.role?.split(",") ?? [];
  if (isLandlord) {
    userRoles.push("landlord");
  }
  if (isPlatformAdmin) {
    userRoles.push("admin");
  }
  const navSections = getNavSectionsForUser(userRoles);

  const tenantLabel = (() => {
    if (!user) return "Tenant Profile";
    // TODO: firstName/lastName are no longer in session (ADR-004)
    // Need to query User model separately if needed, or just use name
    return user.name || "Tenant Profile";
  })();

  const businessLabel = activeBusiness?.businessName || "Business Profile";

  if (!user) return null;

  const handleSignOut = async () => {
    await authClient.signOut();
    await authClient.getSession();
    // Better Auth handles redirect automatically
    router.push("/");
  };

  const switchMenuStyle = async (next: "tenant" | "business") => {
    const previous = menuStyle;
    if (previous !== next) {
      if (previous === "tenant" && next === "business") {
        router.push("/landlord/listings");
      } else if (previous === "business" && next === "tenant") {
        router.push("/favorites");
      }
    }
    setMenuStyle(next);
    onMenuStyleChangeAction?.(next);
  };

  const handleBusinessCreated = async (_businessId: string) => {
    await switchMenuStyle("business");
    setJustCreatedBusiness(true);
    setBusinessModalOpen(false);

    // Redirect to landlord dashboard after successful business creation
    router.push("/landlord");
  };

  const adminMenu = (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="flex items-center gap-2 px-4">
        <span>Admin</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuItem asChild>
          <Link
            href="/admin"
            className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
          >
            <span>Dashboard</span>
          </Link>
        </DropdownMenuItem>
        {navSections
          .filter((section) => section.roles.includes("admin"))
          .map((section) =>
            section.items.map((item) => {
              if (item.href) {
                return (
                  <DropdownMenuItem key={item.title} asChild>
                    <Link
                      href={item.href}
                      className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
                    >
                      <span>{item.title}</span>
                    </Link>
                  </DropdownMenuItem>
                );
              }
              return null;
            }),
          )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  const bottomMenu = (
    <>
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link
            href="/settings"
            className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
          >
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="flex h-12 items-center gap-2 rounded-lg px-4 font-semibold">
            <span className="font-mono text-xs font-medium">Appearance</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              className="flex items-center justify-between rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
              onSelect={(e) => {
                e.preventDefault();
                if (mounted) {
                  setTheme("system");
                }
              }}
            >
              <span
                className={
                  mounted && theme === "system" ? "underline" : undefined
                }
              >
                System
              </span>
              {mounted && theme === "system" && (
                <Check className="ml-auto size-5" />
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center justify-between rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
              onSelect={(e) => {
                e.preventDefault();
                if (mounted) {
                  setTheme("light");
                }
              }}
            >
              <span
                className={
                  mounted && theme === "light" ? "underline" : undefined
                }
              >
                Light
              </span>
              {mounted && theme === "light" && (
                <Check className="ml-auto size-5" />
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center justify-between rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
              onSelect={(e) => {
                e.preventDefault();
                if (mounted) {
                  setTheme("dark");
                }
              }}
            >
              <span
                className={
                  mounted && theme === "dark" ? "underline" : undefined
                }
              >
                Dark
              </span>
              {mounted && theme === "dark" && (
                <Check className="ml-auto size-5" />
              )}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
            onClick={() => onContactUsAction()}
          >
            Contact us
          </button>
        </DropdownMenuItem>
        {!hasBusinessProfile && (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setBusinessModalOpen(true);
            }}
            className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
          >
            <span>Switch to listing</span>
          </DropdownMenuItem>
        )}
        {isPlatformAdmin && <>{adminMenu}</>}
        <DropdownMenuItem
          onClick={handleSignOut}
          className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
        >
          <span className="container truncate">
            Sign out{" "}
            <span className="font-medium no-underline">({user.email})</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );

  return (
    <>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="link"
            className="size-[36px] p-0"
            aria-label="Open user menu"
          >
            <ApartmentDibsAvatar
              user={user}
              optimisticImageUrl={optimisticAvatarUrl}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-65"
          align="end"
          sideOffset={5}
          forceMount
        >
          {showProfileToggle && (
            <>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="flex items-center justify-between rounded-lg"
                  onSelect={(e) => {
                    e.preventDefault();
                    if (menuStyle !== "tenant") {
                      void switchMenuStyle("tenant");
                    }
                  }}
                >
                  <span
                    className={
                      menuStyle !== "tenant"
                        ? "text-muted-foreground"
                        : undefined
                    }
                  >
                    {tenantLabel}
                  </span>
                  {menuStyle === "tenant" && <Check className="ml-2 size-5" />}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex items-center justify-between rounded-lg"
                  onSelect={(e) => {
                    e.preventDefault();
                    if (menuStyle !== "business") {
                      void switchMenuStyle("business");
                    }
                  }}
                >
                  <span
                    className={
                      menuStyle !== "business"
                        ? "text-xs text-muted-foreground"
                        : undefined
                    }
                  >
                    {businessLabel}
                  </span>
                  {menuStyle === "business" && (
                    <Check className="ml-2 size-5" />
                  )}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="-mx-3 my-3" />
            </>
          )}

          {/* Navigation Sections - Show based on current menu style */}
          {menuStyle === "tenant" ? (
            // Show tenant navigation when in tenant menu style
            <>
              {navSections
                .filter(
                  (section) =>
                    section.roles.includes("all") ||
                    section.roles.includes("tenant"),
                )
                .map((section, sectionIndex) => (
                  <div key={section.title}>
                    {sectionIndex > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuGroup>
                      {section.items.map((item) => {
                        if (item.href) {
                          return (
                            <DropdownMenuItem key={item.title} asChild>
                              <Link
                                href={item.href}
                                className="flex cursor-pointer items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
                              >
                                <span>{item.title}</span>
                              </Link>
                            </DropdownMenuItem>
                          );
                        }
                        return null;
                      })}
                    </DropdownMenuGroup>
                  </div>
                ))}
            </>
          ) : (
            // Show business navigation when in business profile
            <>
              {navSections
                .filter(
                  (section) =>
                    section.roles.includes("landlord") &&
                    !section.roles.includes("admin"),
                )
                .map((section, sectionIndex) => (
                  <div key={section.title}>
                    {sectionIndex > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuGroup>
                      {/* Render section items */}
                      {section.items.map((item) => {
                        if (item.href) {
                          return (
                            <DropdownMenuItem key={item.title} asChild>
                              <Link
                                href={item.href}
                                className="flex cursor-pointer items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
                              >
                                <span>{item.title}</span>
                              </Link>
                            </DropdownMenuItem>
                          );
                        } else if (item.component) {
                          return (
                            <DropdownMenuItem
                              key={item.title}
                              onSelect={(e) => {
                                e.preventDefault();
                                // Handle component-based items (like modals)
                                if (item.title === "Create Listing") {
                                  router.push(`/landlord/listings/new`);
                                }
                              }}
                              className="flex items-center gap-2 rounded-lg font-mono text-xs text-foreground underline-offset-1 hover:underline"
                            >
                              {item.title}
                            </DropdownMenuItem>
                          );
                        }
                        return null;
                      })}
                    </DropdownMenuGroup>
                  </div>
                ))}
            </>
          )}

          {bottomMenu}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Business Registration Modal */}
      <BusinessFormModal
        open={businessModalOpen}
        onOpenChange={setBusinessModalOpen}
        onSuccess={handleBusinessCreated}
        title="Create a business"
      />
    </>
  );
}
